const DEFAULT_TYPE_SPEED = 30;
const FAST_TYPE_SPEED = 8;

interface TerminalConfig {
  readonly outputElement: HTMLElement;
  readonly containerElement: HTMLElement;
}

interface TypeOptions {
  readonly speed?: number;
  readonly cssClass?: string;
  readonly newline?: boolean;
}

interface Terminal {
  print(text: string, options?: TypeOptions): Promise<void>;
  printImmediate(text: string, cssClass?: string): void;
  println(text: string, options?: TypeOptions): Promise<void>;
  waitForInput(prompt?: string): Promise<string>;
  waitForKey(): Promise<string>;
  clear(): void;
  pause(ms: number): Promise<void>;
  setTypingSpeed(speed: number): void;
  showCursor(): void;
  hideCursor(): void;
  destroy(): void;
}

function createTerminal(config: TerminalConfig): Terminal {
  const { outputElement, containerElement } = config;
  let baseTypingSpeed = DEFAULT_TYPE_SPEED;
  let cursorElement: HTMLSpanElement | null = null;
  let inputResolve: ((value: string) => void) | null = null;
  let keyResolve: ((value: string) => void) | null = null;
  let currentInputText = '';
  let inputSpan: HTMLSpanElement | null = null;
  let destroyed = false;

  function ensureCursor(): HTMLSpanElement {
    if (!cursorElement) {
      cursorElement = document.createElement('span');
      cursorElement.className = 'cursor';
      cursorElement.textContent = '\u00A0';
      outputElement.appendChild(cursorElement);
    }
    return cursorElement;
  }

  function removeCursor(): void {
    if (cursorElement && cursorElement.parentNode) {
      cursorElement.parentNode.removeChild(cursorElement);
      cursorElement = null;
    }
  }

  function scrollToBottom(): void {
    containerElement.scrollTop = containerElement.scrollHeight;
  }

  function appendText(text: string, cssClass?: string): HTMLSpanElement {
    removeCursor();
    const span = document.createElement('span');
    if (cssClass) span.className = cssClass;
    span.textContent = text;
    outputElement.appendChild(span);
    ensureCursor();
    scrollToBottom();
    return span;
  }

  function typeText(text: string, speed: number, cssClass?: string): Promise<void> {
    return new Promise((resolve) => {
      if (destroyed) { resolve(); return; }
      removeCursor();
      const span = document.createElement('span');
      if (cssClass) span.className = cssClass;
      span.textContent = '';
      outputElement.appendChild(span);
      ensureCursor();

      let i = 0;
      function typeNext(): void {
        if (destroyed || i >= text.length) {
          resolve();
          return;
        }
        span.textContent += text[i];
        i++;
        scrollToBottom();
        const jitter = speed + (Math.random() * speed * 0.4 - speed * 0.2);
        setTimeout(typeNext, Math.max(1, jitter));
      }
      typeNext();
    });
  }

  function handleKeyDown(e: KeyboardEvent): void {
    if (keyResolve) {
      e.preventDefault();
      const resolve = keyResolve;
      keyResolve = null;
      resolve(e.key);
      return;
    }

    if (!inputResolve) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      const resolve = inputResolve;
      const text = currentInputText;
      inputResolve = null;
      currentInputText = '';
      inputSpan = null;
      appendText('\n');
      resolve(text);
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      if (currentInputText.length > 0) {
        currentInputText = currentInputText.slice(0, -1);
        if (inputSpan) {
          inputSpan.textContent = currentInputText;
        }
      }
      return;
    }

    if (e.key.length === 1) {
      e.preventDefault();
      currentInputText += e.key.toUpperCase();
      if (inputSpan) {
        inputSpan.textContent = currentInputText;
      }
      scrollToBottom();
    }
  }

  document.addEventListener('keydown', handleKeyDown);

  const terminal: Terminal = {
    async print(text: string, options: TypeOptions = {}): Promise<void> {
      const speed = options.speed ?? baseTypingSpeed;
      await typeText(text, speed, options.cssClass);
      if (options.newline !== false) {
        // no auto newline unless explicitly via println
      }
    },

    printImmediate(text: string, cssClass?: string): void {
      appendText(text, cssClass);
    },

    async println(text: string, options: TypeOptions = {}): Promise<void> {
      const speed = options.speed ?? baseTypingSpeed;
      await typeText(text + '\n', speed, options.cssClass);
    },

    waitForInput(prompt = ''): Promise<string> {
      return new Promise((resolve) => {
        if (prompt) {
          appendText(prompt);
        }
        removeCursor();
        inputSpan = document.createElement('span');
        inputSpan.className = 'input-text';
        inputSpan.textContent = '';
        outputElement.appendChild(inputSpan);
        ensureCursor();
        currentInputText = '';
        inputResolve = resolve;
        scrollToBottom();
      });
    },

    waitForKey(): Promise<string> {
      return new Promise((resolve) => {
        keyResolve = resolve;
      });
    },

    clear(): void {
      removeCursor();
      outputElement.innerHTML = '';
      ensureCursor();
    },

    pause(ms: number): Promise<void> {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    setTypingSpeed(speed: number): void {
      baseTypingSpeed = speed;
    },

    showCursor(): void {
      const cursor = ensureCursor();
      cursor.classList.remove('hidden');
    },

    hideCursor(): void {
      if (cursorElement) {
        cursorElement.classList.add('hidden');
      }
    },

    destroy(): void {
      destroyed = true;
      document.removeEventListener('keydown', handleKeyDown);
      removeCursor();
    },
  };

  ensureCursor();
  return terminal;
}

export { createTerminal, DEFAULT_TYPE_SPEED, FAST_TYPE_SPEED };
export type { Terminal, TypeOptions };
